/**
 * Format a numeric KPI value for display.
 * currency: $1.2M / $76.78 (abbreviated above 1000)
 * count: 2.3K / 88
 * percent: 10.57%
 */
import { clampKpiDecimals } from "@/features/kpi-settings/lib/kpi-decimals";

export function formatMetricValue(
  value: number,
  format: "currency" | "count" | "percent" | "ratio",
  currencySymbol = "$",
  decimals = 2,
): string {
  const safeDecimals = clampKpiDecimals(decimals);
  const v = value ?? 0;

  if (format === "ratio") {
    return `${v.toFixed(safeDecimals)}x`;
  }

  if (format === "percent") {
    return `${v.toFixed(safeDecimals)}%`;
  }

  const prefix = format === "currency" ? currencySymbol : "";

  if (Math.abs(v) >= 1_000_000) {
    return `${prefix}${(v / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(v) >= 1_000) {
    return `${prefix}${(v / 1_000).toFixed(1)}K`;
  }
  if (format === "count") {
    if (v !== 0 && Math.abs(v) < 1) {
      return v.toFixed(Math.max(safeDecimals, 2));
    }
    if (safeDecimals === 0) return Math.round(v).toLocaleString();
    return Number(v.toFixed(safeDecimals)).toLocaleString();
  }
  return `${prefix}${v.toFixed(safeDecimals)}`;
}

/** Percentage change from previous to current. Returns 0 when previous is 0. */
export function computePctChange(current: number, previous: number): number {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

export interface BenchmarkDelta {
  hasBenchmark: boolean;
  percent: number;
  isPositive: boolean;
}

/** Delta of `you` vs benchmark for competitor KPI footer rows. */
export function buildBenchmarkDelta(you: number, benchmark: number): BenchmarkDelta {
  if (benchmark <= 0) {
    return { hasBenchmark: false, percent: 0, isPositive: false };
  }
  const percent = computePctChange(you, benchmark);
  return {
    hasBenchmark: true,
    percent,
    isPositive: percent > 0,
  };
}

/** Format a count KPI value as a localized integer (no K/M abbreviation). */
export function formatCountValue(value: number): string {
  return Math.round(value).toLocaleString();
}

/** Humanize BQ/filter slug keys for display (e.g. solitare_classic → Solitare Classic). */
export function formatBrandName(name: string): string {
  return name
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}
